import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, Bed, Bath, Maximize, MapPin, Download } from "lucide-react";
import api, { API, fileUrl } from "../lib/api";
import { PageHeader, Loading, Empty, Pill, Stamp } from "../components/kit";
import { Input, Select } from "../components/form";
import { Button } from "../components/ui/button";
import PropertyForm from "../components/PropertyForm";
import PropertiesMap from "../components/PropertiesMap";
import { useAuth } from "../context/AuthContext";
import { PROPERTY_TYPES, PROPERTY_STATUSES, money, titleCase } from "../lib/constants";

const STATUS_COLOR = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  for_sale: "bg-emerald-100 text-emerald-700 border-emerald-200",
  fsbo: "bg-amber-100 text-amber-700 border-amber-200",
  pending: "bg-sky-100 text-sky-700 border-sky-200",
  under_contract: "bg-violet-100 text-violet-700 border-violet-200",
  sold: "bg-slate-200 text-slate-600 border-slate-300",
};

export default function Properties() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [view, setView] = useState("grid");
  const nav = useNavigate();

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (status) p.set("status", status);
    if (type) p.set("property_type", type);
    const { data } = await api.get(`/properties?${p.toString()}`);
    setData(data);
  }, [search, status, type]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Properties" subtitle="Every listing, FSBO, and investment property — connected to owners and deals." testid="properties-header"
        actions={<>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">{["grid", "map"].map((v) => <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`} className={`px-3 py-2 text-sm font-medium capitalize ${view === v ? "bg-navy-800 text-white" : "bg-white text-slate-600"}`}>{v}</button>)}</div>
          {user?.role === "master" && <Button variant="outline" onClick={() => window.open(`${API}/export/properties?auth=${localStorage.getItem("gas_token")}`)} data-testid="export-properties-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button>}
          <Button className="bg-navy-800 hover:bg-navy-700 text-white" onClick={() => setShowForm(true)} data-testid="new-property-btn"><Plus className="w-4 h-4 mr-1.5" />New Property</Button>
        </>} />

      {view === "map" ? <PropertiesMap /> : <>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search address, city, MLS…" className="pl-9" data-testid="properties-search" /></div>
        <Select options={[{ value: "", label: "All statuses" }, ...PROPERTY_STATUSES.map((s) => ({ value: s, label: titleCase(s) }))]} value={status} onChange={setStatus} testid="filter-status" />
        <Select options={[{ value: "", label: "All types" }, ...PROPERTY_TYPES.map((s) => ({ value: s, label: titleCase(s) }))]} value={type} onChange={setType} testid="filter-ptype" />
      </div>

      {!data ? <Loading /> : data.items.length === 0 ? (
        <Empty title="No properties" subtitle="Add your first property listing." action={<Button className="bg-navy-800 text-white" onClick={() => setShowForm(true)}>New Property</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="properties-grid">
          {data.items.map((p) => <PropertyCard key={p.id} p={p} onClick={() => nav(`/properties/${p.id}`)} />)}
        </div>
      )}
      </>}

      <PropertyForm open={showForm} onClose={() => setShowForm(false)} onSaved={load} />
    </div>
  );
}

function PropertyCard({ p, onClick }) {
  const img = p.cover_file_id ? fileUrl(p.cover_file_id) : p.cover_image_url || null;
  return (
    <motion.div whileHover={{ y: -4 }} onClick={onClick} data-testid={`property-card-${p.id}`}
      className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-amber-500/40 transition-[box-shadow,border-color] cursor-pointer overflow-hidden">
      <div className="h-44 relative bg-gradient-to-br from-navy-800 to-navy-900 flex items-center justify-center overflow-hidden">
        {img ? <img src={img} alt={p.address} className="w-full h-full object-cover" loading="lazy" data-testid={`property-cover-${p.id}`} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <MapPin className="w-10 h-10 text-white/20" />}
        {p.cover_is_placeholder && <span className="absolute bottom-3 right-3 bg-white/85 text-navy-900 text-[9px] font-bold px-1.5 py-0.5 rounded" data-testid={`sample-photo-badge-${p.id}`}>SAMPLE PHOTO</span>}
        {p.is_test && <span className="absolute top-3 right-3 bg-amber-400 text-navy-900 text-[10px] font-extrabold px-1.5 py-0.5 rounded shadow" data-testid={`test-badge-${p.id}`}>TEST DATA</span>}
        <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLOR[p.status] || "bg-white text-slate-600 border-slate-200"}`}>{titleCase(p.status || "active")}</span>
        <span className="absolute bottom-3 left-3 font-display text-white text-xl font-bold drop-shadow">{p.asking_price ? money(p.asking_price) : p.estimated_value ? <span>EMV {money(p.estimated_value)}</span> : "—"}</span>
        {p.distress_type && <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded shadow" data-testid={`distress-badge-${p.id}`}>{p.distress_type.toUpperCase()}{p.auction_date ? ` · AUCTION ${p.auction_date}` : ""}</span>}
      </div>
      <div className="p-4">
        <p className="font-display font-semibold text-navy-800 truncate">{p.address}</p>
        <p className="text-xs text-slate-400 mb-3">{[p.city, p.state, p.zip].filter(Boolean).join(", ")}</p>
        <div className="flex items-center gap-4 text-sm text-slate-500">
          <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{p.beds || 0}</span>
          <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{p.baths || 0}</span>
          <span className="flex items-center gap-1"><Maximize className="w-3.5 h-3.5" />{p.square_feet ? `${p.square_feet} sf` : "—"}</span>
          {p.days_on_market > 0 && <span className="ml-auto text-xs">{p.days_on_market} DOM</span>}
        </div>
        {p.tags?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3">{p.tags.slice(0, 3).map((t) => <Pill key={t} className="text-[10px]">{t}</Pill>)}</div>}
        <div className="mt-3 pt-2.5 border-t border-slate-100"><Stamp prefix="Added by" by={p.created_by_name || "System"} at={p.created_at} testid={`property-stamp-${p.id}`} /></div>
      </div>
    </motion.div>
  );
}
